/** Your streak: the year's calendar lights up day by day, then your longest run glows. */

import type { CSSProperties } from "react";

import { Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { tokenValue } from "../../theme";
import type { Period, You } from "../../types";
import { shortDate } from "../shared";

const BLOBS: BlobSpec[] = [{ colour: "deep", x: 980, y: 1700, r: 330, seed: 10.4, wob: 0.16 }];

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const GLOW = "0 0 16px 2px var(--wr-glow)";

const iso = (at: Date) => at.toISOString().slice(0, 10);

type Month = { label: string; blanks: number; days: string[] };

/** The period's months, each with its days in the period and the blanks before its first Monday. */
function monthsOf(period: Period): Month[] {
  const [y, m, d] = period.start.split("-").map(Number);
  const at = new Date(Date.UTC(y, m - 1, d));
  const months: Month[] = [];
  for (let day = iso(at); day < period.end; at.setUTCDate(at.getUTCDate() + 1), day = iso(at)) {
    const month = at.getUTCMonth();
    let current = months[months.length - 1];
    if (!current || current.days[0].slice(0, 7) !== day.slice(0, 7)) {
      // The year goes on the first month and on every January.
      const label = months.length === 0 || month === 0 ? `${MONTHS[month]} '${day.slice(2, 4)}` : MONTHS[month];
      const first = new Date(Date.UTC(at.getUTCFullYear(), month, 1));
      // Days before the period's first one stay blank, so dates keep their weekday column.
      current = { label, blanks: (first.getUTCDay() + 6) % 7 + (at.getUTCDate() - 1), days: [] };
      months.push(current);
    }
    current.days.push(day);
  }
  return months;
}

export function Streak({ streak, period }: { streak: NonNullable<You["streak"]>; period: Period }) {
  const months = monthsOf(period);
  const total = months.reduce((n, month) => n + month.days.length, 0);
  const active = new Set(streak.active_days);
  const activeCount = months.reduce((n, month) => n + month.days.filter((day) => active.has(day)).length, 0);
  const inRun = (day: string) => day >= streak.start && day <= streak.end;
  // A period that starts mid-month spans thirteen months: five to a row, smaller days.
  const compact = months.length > 12;
  const cell = compact ? 20 : 27;
  const gap = compact ? 4 : 5;
  const when = streak.start === streak.end ? shortDate(streak.start) : `${shortDate(streak.start)} to ${shortDate(streak.end)}`;

  const root = useTimeline((s) => {
    s.rise(["[data-part=kick]", "[data-part=lead]"], 0.35, { y: 30, stagger: 0.12 });
    s.tl.fromTo(".cal", { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 0.45);

    const cells = s.q(".day");
    const run = cells.filter((c) => c.dataset.run === "true");
    const lit = (c: HTMLElement) => c.dataset.active === "true" || c.dataset.run === "true";
    const green = tokenValue("green");
    const missed = tokenValue("missed");
    const glow = tokenValue("glow");
    // Every day lights in order, the run green for now; it turns yellow once the count is done.
    s.tl.fromTo(
      cells,
      { backgroundColor: tokenValue("off") },
      { backgroundColor: (i: number) => (lit(cells[i]) ? green : missed), duration: 0.16, stagger: 1.8 / cells.length, ease: "none" },
      0.6,
    );
    // A long run still glows in under a second.
    const glowIn = { duration: 0.25, stagger: Math.min(0.012, 0.8 / Math.max(run.length, 1)), ease: "power2.out" };
    s.tl.fromTo(run, { boxShadow: `0 0 0 0 ${glow}`, scale: 1 }, { boxShadow: `0 0 16px 2px ${glow}`, scale: 1.18, ...glowIn }, 2.5);
    s.tl.to(run, { backgroundColor: tokenValue("yellow"), ...glowIn }, 2.5);
    s.sound("sparkle", 2.5);

    // The counter follows the light: active days so far, out of the whole year.
    const counter = s.q("[data-part=count] b")[0];
    const before = [0];
    cells.forEach((c, i) => before.push(before[i] + (c.dataset.active === "true" ? 1 : 0)));
    const fill = { n: 0 };
    if (counter) {
      counter.textContent = "0";
      s.tl.to(fill, { n: cells.length, duration: 1.8, ease: "none", onUpdate: () => (counter.textContent = String(before[Math.floor(fill.n)])) }, 0.6);
    }
    // The days light up at a steady pace: a steady run of clicks under a rising zip.
    for (let k = 0; k < 12; k++) s.sound("tick", 0.6 + k * 0.15, 1 + k * 0.04);
    s.sound("zip", 0.6, 0.8, { length: 1.8 });
    s.rise("[data-part=count]", 0.6, { y: 20, sound: false });

    s.tl.fromTo(".s-big", { scale: 0.2, autoAlpha: 0, transformOrigin: "0% 100%" }, { scale: 1, autoAlpha: 1, duration: 0.6, ease: "back.out(2.2)" }, 2.95);
    s.sound("pop", 2.95, 1.3);
    s.rise(".s-unit", 3.15, { y: 30, sound: false });
    s.rise("[data-part=when]", 3.45, { y: 0, sound: false });
  });

  const cal: CSSProperties = compact
    ? { gridTemplateColumns: `repeat(5, ${7 * cell + 6 * gap}px)`, columnGap: 24, rowGap: 22 }
    : {};

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick" style={{ color: "var(--wr-green)" }}>
            Your streak
          </div>
          <div data-part="lead" className="lead">
            Every day you showed up
          </div>
        </Zone>
        {/* The calendar and its count fill the middle; the run it found reads as the foot. */}
        <Zone at="middle">
          <div className="cal" style={cal}>
            {months.map((month) => (
              <div key={month.days[0]} className="mon">
                <b>{month.label}</b>
                <div className="days" style={compact ? { gridTemplateColumns: `repeat(7, ${cell}px)`, gap } : undefined}>
                  {Array.from({ length: month.blanks }, (_, i) => (
                    <span key={`blank-${i}`} />
                  ))}
                  {month.days.map((day) => {
                    const on = active.has(day);
                    const run = inRun(day);
                    const look: CSSProperties = run
                      ? { background: "var(--wr-yellow)", boxShadow: GLOW, transform: "scale(1.18)" }
                      : { background: on ? "var(--wr-green)" : "var(--wr-missed)" };
                    return (
                      <span
                        key={day}
                        data-testid="day"
                        data-active={on}
                        data-run={run}
                        className="day"
                        style={compact ? { ...look, width: cell, height: cell, borderRadius: 5 } : look}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div data-part="count" className="line" style={{ top: 1180 }}>
            <b style={{ color: "var(--wr-green)" }}>{activeCount}</b> of {total} days
          </div>
        </Zone>
        <Zone at="bottom">
          <div className="s-row">
            <div className="s-big disp">{streak.days}</div>
            <div className="s-unit disp">
              {streak.days === 1 ? "day " : "days "}
              <br />
              in a row
            </div>
          </div>
          <div data-part="when" className="kick" style={{ top: 1610, color: "var(--wr-yellow)" }}>
            {when} · your longest run
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
